/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	CosmosComponentContextProps,
	DividerSection,
	getHero,
	getSections,
	useCosmosComponentContext,
} from "@cosmos/components";
import { CosmosEnumDirectoryVariant, CosmosEnumSectionSizes } from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";

import { TemplateProviders } from "../templates/TemplateProviders";

import { DirectoryCmsBody } from "./DirectoryCmsBody";
import { InstructorSpotlight } from "./InstructorSpotlight";

import type { DirectoryCmsBodyData, DirectoryBaseChrome, DirectoryServiceChrome } from "./DirectoryCmsBody";
import type { DirectoryPageData } from "./types";

interface Props {
	pageData: DirectoryPageData;
	componentContext?: CosmosComponentContextProps;
}

/**
 * Page-data → `DirectoryCmsBody` data shape. Cast-free translation:
 * the discriminated `switch` lets TS verify each variant's chrome shape.
 *
 * Hygraph fragment fields are prefixed with `directory*` for content fields;
 * we strip the prefix on the chrome side because the surrounding type
 * (`DirectoryBaseChrome`) already namespaces them.
 */
function toCmsBodyData(pageData: DirectoryPageData): DirectoryCmsBodyData {
	const baseChrome: DirectoryBaseChrome = {
		eyebrow: pageData.directoryEyebrow,
		title: pageData.directoryTitle,
		descHtml: pageData.directoryDesc,
		searchPlaceholder: pageData.searchPlaceholder,
		industryFilterLabel: pageData.industryFilterLabel,
		noResultsLabel: pageData.noResultsLabel,
	};

	switch (pageData.variant) {
		case CosmosEnumDirectoryVariant.GoldInstructors:
			return { variant: pageData.variant, cards: pageData.cards, chrome: baseChrome };
		case CosmosEnumDirectoryVariant.TrainingPartners:
			return { variant: pageData.variant, cards: pageData.cards, chrome: baseChrome };
		case CosmosEnumDirectoryVariant.ServicePartners: {
			// English fallback so the rendered combobox always has an accessible
			// name even when the CMS field is null. Localized override comes from
			// `pageData.serviceFilterLabel` when set.
			const serviceChrome: DirectoryServiceChrome = {
				...baseChrome,
				serviceFilterLabel: pageData.serviceFilterLabel ?? "Services",
				serviceFilterPlaceholder: pageData.serviceFilterPlaceholder,
			};
			return { variant: pageData.variant, cards: pageData.cards, chrome: serviceChrome };
		}
		default: {
			const exhaustiveCheck: never = pageData;
			throw new Error(
				`Unhandled DirectoryPageData variant: ${String((exhaustiveCheck as { variant?: string }).variant)}`,
			);
		}
	}
}

/**
 * Client-side renderer for `CosmosPageDirectory`.
 *
 * Composition order on the page:
 *   1. Hero
 *   2. topRefSections (in editor-defined order — includes the spotlight
 *      heading for GoldInstructors and the grid heading for all variants)
 *   3. Weekly Spotlight strip (GoldInstructors variant only — page-template
 *      injects under the last topRefSection, where the editor places the
 *      spotlight heading section)
 *   4. Directory grid body (filter chrome + cards + pagination)
 *   5. bottomRefSections
 */
export const CosmosPageDirectoryClient = ({ pageData, componentContext }: Props): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);

	return (
		<TemplateProviders componentContext={ctx}>
			{getHero(pageData.hero)}
			{getSections(pageData.topRefSections)}
			{/*
				Weekly Spotlight injects between topRefSections and the main grid for
				the GoldInstructors variant only. The editor places the heading section
				at the end of topRefSections; the cards render directly below it.
			*/}
			{pageData.variant === CosmosEnumDirectoryVariant.GoldInstructors ? (
				<>
					<InstructorSpotlight instructors={pageData.spotlight} />
					{/*
						Divider visually separates the Weekly Spotlight strip from the main
						directory grid. `DividerSection` uses `disablePaddingBlock` so the
						spacing comes from neighboring sections — splitting evenly between
						the spotlight's padding-block-end and the grid's padding-block-start.
					*/}
					<DividerSection size={CosmosEnumSectionSizes.Sm} edsBackgroundColor={null} />
				</>
			) : null}
			<DirectoryCmsBody data={toCmsBodyData(pageData)} />
			{getSections(pageData.bottomRefSections)}
		</TemplateProviders>
	);
};
